import React, { useCallback, useEffect } from 'react';

import { useTranslation } from '@lyearn/i18n';
import { Close, Search } from '@lyearn/icons';
import { LinkButton, Stack, Typography } from '@lyearn/ui';
import useDebounceState from '@/hooks/useDebounceState';

import SearchInput from './SearchInput';
import type { SearchBarWithCountProps } from './types';

const SHORTCUT_KEY = '/';
function SearchBarWithCount(props: SearchBarWithCountProps) {
  const { t } = useTranslation();
  const { value, onChange, selectedCount = 0 } = props;
  const [searchText, setSearchText, debounceSearchText] = useDebounceState(value);

  useEffect(() => {
    onChange(debounceSearchText);
  }, [debounceSearchText, onChange]);

  const onClear = useCallback(() => {
    setSearchText('');
  }, [setSearchText]);

  useEffect(() => {
    if (value === '') {
      onClear();
    }
  }, [onClear, value]);

  const endAdornment = value.length ? (
    <LinkButton className="py-2 px-4 px-0" color={'internal'} onClick={onClear}>
      <Close className="text-icon-placeholder hover:text-icon-active" height={20} width={20} />
    </LinkButton>
  ) : selectedCount ? (
    <Typography className="flex-shrink-0 me-8" variant="body-short-02">
      {t('Common:SelectedCount', {
        count: selectedCount,
      })}
    </Typography>
  ) : (
    <Stack
      alignItems="center"
      className="h-24 w-24 rounded-4 bg-bg-tertiary px-8 py-2"
      justifyContent="center">
      <Typography color="text-text-placeholder" variant="body-short-02">
        {SHORTCUT_KEY}
      </Typography>
    </Stack>
  );

  return (
    <SearchInput
      {...props}
      endAdornment={endAdornment}
      startAdornment={
        <Search
          className="flex-shrink-0 text-icon-placeholder xs:text-icon-secondary"
          height={20}
          width={20}
        />
      }
      value={searchText}
      onChange={setSearchText}
    />
  );
}

export default React.memo(SearchBarWithCount);
